import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { PlusIcon, PencilIcon, TrashIcon, PhotoIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { productsAPI, getErrorMessage } from "../../services/api";
import AdminLayout from "../../components/admin/AdminLayout";
import LoadingSpinner from "../../components/ui/LoadingSpinner";
import { resolveImageUrl } from "../../utils/image";
import toast from "react-hot-toast";

const SIZES = ["XS", "S", "M", "L", "XL", "XXL"];

const PRESET_COLORS = [
  { name: "Red", hex_code: "#EF4444" },
  { name: "Blue", hex_code: "#3B82F6" },
  { name: "Green", hex_code: "#22C55E" },
  { name: "Yellow", hex_code: "#EAB308" },
  { name: "Pink", hex_code: "#EC4899" },
  { name: "Purple", hex_code: "#A855F7" },
  { name: "Orange", hex_code: "#F97316" },
  { name: "Black", hex_code: "#1F2937" },
  { name: "White", hex_code: "#F9FAFB" },
  { name: "Gray", hex_code: "#6B7280" },
  { name: "Navy", hex_code: "#1E3A5F" },
  { name: "Maroon", hex_code: "#800000" },
];

/** One row in our color×size matrix */
interface ColorRow {
  name: string;
  hex_code: string;
  stock: Record<string, number>; // size → stock
}

function makeEmptyRow(name: string, hex_code: string): ColorRow {
  return { name, hex_code, stock: Object.fromEntries(SIZES.map(s => [s, 0])) };
}

export default function AdminProducts() {
  const [products, setProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editProduct, setEditProduct] = useState<any>(null);

  // form state
  const [form, setForm] = useState({
    name: "", description: "", price: "", original_price: "",
    category: "", material: "", is_featured: false,
  });
  const [colorRows, setColorRows] = useState<ColorRow[]>([]);
  const [newColorName, setNewColorName] = useState("");
  const [newColorHex, setNewColorHex] = useState("#EC4899");

  const [saving, setSaving] = useState(false);
  const [uploadingFor, setUploadingFor] = useState<string | null>(null);
  const [manageImagesFor, setManageImagesFor] = useState<any>(null);
  const [uploadColorFor, setUploadColorFor] = useState<string>("");   // color tag for next upload
  const MAX_IMAGES = 8;

  useEffect(() => { loadProducts(); }, []);

  const loadProducts = async () => {
    try {
      const res = await productsAPI.list({ page_size: 100 });
      setProducts(res.data.items || []);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({ name: "", description: "", price: "", original_price: "", category: "", material: "", is_featured: false });
    setColorRows([]);
    setNewColorName("");
    setNewColorHex("#EC4899");
    setEditProduct(null);
  };

  const handleEdit = (product: any) => {
    setEditProduct(product);
    setForm({
      name: product.name,
      description: product.description || "",
      price: String(product.price),
      original_price: product.original_price ? String(product.original_price) : "",
      category: product.category || "",
      material: product.material || "",
      is_featured: product.is_featured,
    });

    // Rebuild colorRows from variants
    const rowMap: Record<string, ColorRow> = {};
    for (const v of (product.variants || [])) {
      const key = v.color_name || "";
      if (!rowMap[key]) {
        rowMap[key] = makeEmptyRow(v.color_name || "", v.hex_code || "#EC4899");
      }
      rowMap[key].stock[v.size] = v.stock;
    }
    setColorRows(Object.values(rowMap));
    setNewColorName("");
    setNewColorHex("#EC4899");
    setShowForm(true);
  };

  const addColorRow = (name: string, hex_code: string) => {
    if (!name.trim()) return;
    if (colorRows.some(r => r.name.toLowerCase() === name.trim().toLowerCase())) {
      toast.error(`Color "${name}" already added`);
      return;
    }
    setColorRows(prev => [...prev, makeEmptyRow(name.trim(), hex_code)]);
    setNewColorName("");
  };

  const removeColorRow = (idx: number) => {
    setColorRows(prev => prev.filter((_, i) => i !== idx));
  };

  const updateStock = (rowIdx: number, size: string, value: number) => {
    setColorRows(prev => prev.map((row, i) =>
      i === rowIdx ? { ...row, stock: { ...row.stock, [size]: value } } : row
    ));
  };

  /** Flatten colorRows → variants array for the API */
  const buildVariants = () => {
    const variants: any[] = [];
    for (const row of colorRows) {
      for (const size of SIZES) {
        variants.push({
          color_name: row.name,
          hex_code: row.hex_code,
          size,
          stock: row.stock[size] ?? 0,
        });
      }
    }
    return variants;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (colorRows.length === 0) {
      toast.error("Add at least one color (or a 'No Color' row) with sizes");
      return;
    }
    setSaving(true);
    try {
      const data = {
        name: form.name,
        description: form.description || null,
        price: parseFloat(form.price),
        original_price: form.original_price ? parseFloat(form.original_price) : null,
        category: form.category || null,
        material: form.material || null,
        is_featured: form.is_featured,
        variants: buildVariants(),
      };
      if (editProduct) {
        await productsAPI.update(editProduct.id, data);
        toast.success("Product updated!");
      } else {
        await productsAPI.create(data);
        toast.success("Product created!");
      }
      setShowForm(false);
      resetForm();
      loadProducts();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await productsAPI.delete(id);
      toast.success("Product deleted");
      loadProducts();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const handleDeleteImage = async (productId: string, imageId: string) => {
    if (!window.confirm("Delete this image?")) return;
    try {
      await productsAPI.deleteImage(productId, imageId);
      toast.success("Image deleted");
      const updated = await productsAPI.get(productId);
      setManageImagesFor(updated.data);
      loadProducts();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  // ── total stock across all colorRows
  const totalStock = colorRows.reduce((sum, row) =>
    sum + SIZES.reduce((s2, sz) => s2 + (row.stock[sz] || 0), 0), 0);

  return (
    <AdminLayout>
      <Helmet><title>Products – Admin | Kushi Fashions</title></Helmet>

      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Products</h1>
        <button
          onClick={() => { resetForm(); setShowForm(true); }}
          className="btn-primary flex items-center gap-2"
        >
          <PlusIcon className="h-5 w-5" /> Add Product
        </button>
      </div>

      {/* Manage Images modal */}
      {manageImagesFor && (() => {
        // unique colors this product has (from variants)
        const productColors: { name: string; hex_code: string }[] = [];
        for (const v of (manageImagesFor.variants || [])) {
          if (v.color_name && !productColors.some((c: any) => c.name === v.color_name)) {
            productColors.push({ name: v.color_name, hex_code: v.hex_code });
          }
        }
        return (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Manage Images</h2>
                  <p className="text-xs text-gray-400">{manageImagesFor.images?.length || 0} / {MAX_IMAGES} images</p>
                </div>
                <button onClick={() => setManageImagesFor(null)} className="text-gray-400 hover:text-gray-600 text-xl font-bold">✕</button>
              </div>

              {/* Color tag selector for upload */}
              {productColors.length > 0 && (
                <div className="mb-4 p-3 bg-gray-50 rounded-xl">
                  <p className="text-xs font-medium text-gray-600 mb-2">Tag next upload to color:</p>
                  <div className="flex gap-2 flex-wrap">
                    <button
                      type="button"
                      onClick={() => setUploadColorFor("")}
                      className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                        uploadColorFor === "" ? "bg-gray-700 text-white border-gray-700" : "border-gray-300 text-gray-600 hover:border-gray-500"
                      }`}
                    >
                      All colors
                    </button>
                    {productColors.map(c => (
                      <button
                        key={c.name}
                        type="button"
                        onClick={() => setUploadColorFor(c.name)}
                        className={`flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-full border transition-colors ${
                          uploadColorFor === c.name ? "bg-primary-600 text-white border-primary-600" : "border-gray-300 text-gray-600 hover:border-primary-400"
                        }`}
                      >
                        <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: c.hex_code }} />
                        {c.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-3 gap-3 mb-4">
                {(manageImagesFor.images || []).map((img: any) => {
                  const imgColor = productColors.find(c => c.name === img.color_name);
                  return (
                    <div key={img.id} className="relative group">
                      <img src={resolveImageUrl(img.url)} alt="" className="w-full h-28 object-cover rounded-lg border border-gray-200" />
                      {/* badges */}
                      <div className="absolute top-1 left-1 flex flex-col gap-0.5">
                        {img.is_primary && (
                          <span className="text-xs bg-primary-600 text-white px-1.5 py-0.5 rounded leading-tight">Primary</span>
                        )}
                        {img.color_name ? (
                          <span
                            className="text-xs text-white px-1.5 py-0.5 rounded leading-tight flex items-center gap-1"
                            style={{ backgroundColor: imgColor?.hex_code ?? "#6B7280" }}
                          >
                            {img.color_name}
                          </span>
                        ) : (
                          <span className="text-xs bg-gray-500 text-white px-1.5 py-0.5 rounded leading-tight">All</span>
                        )}
                      </div>
                      <button
                        onClick={() => handleDeleteImage(manageImagesFor.id, img.id)}
                        className="absolute bottom-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <TrashIcon className="h-3 w-3" />
                      </button>
                    </div>
                  );
                })}
                {(manageImagesFor.images?.length || 0) < MAX_IMAGES && (
                  <label className="h-28 border-2 border-dashed border-gray-200 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-primary-400 transition-colors">
                    <PhotoIcon className="h-6 w-6 text-gray-300 mb-1" />
                    <span className="text-xs text-gray-400">
                      {uploadColorFor ? `Add for ${uploadColorFor}` : "Add image"}
                    </span>
                    <input
                      type="file" accept="image/*" className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        setUploadingFor(manageImagesFor.id);
                        const fd = new FormData();
                        fd.append("file", file);
                        fd.append("is_primary", manageImagesFor.images?.length === 0 ? "true" : "false");
                        fd.append("color_name", uploadColorFor);
                        try {
                          await productsAPI.uploadImage(manageImagesFor.id, fd);
                          toast.success("Image uploaded!");
                          const updated = await productsAPI.get(manageImagesFor.id);
                          setManageImagesFor(updated.data);
                          loadProducts();
                        } catch (err) {
                          toast.error(getErrorMessage(err));
                        } finally {
                          setUploadingFor(null);
                          e.target.value = "";
                        }
                      }}
                    />
                  </label>
                )}
              </div>
              <p className="text-xs text-gray-400">
                "All" badge = shown for every color. Hover an image and click trash to delete. Max {MAX_IMAGES} images.
              </p>
            </div>
          </div>
        );
      })()}

      {/* Product form modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-5">
              {editProduct ? "Edit Product" : "Add New Product"}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-5">

              {/* Basic fields */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Product Name *</label>
                  <input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required className="input-field" />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={3} className="input-field resize-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Price (₹) *</label>
                  <input type="number" value={form.price} onChange={e => setForm(f => ({ ...f, price: e.target.value }))} required min="1" step="0.01" className="input-field" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Original Price (₹)</label>
                  <input type="number" value={form.original_price} onChange={e => setForm(f => ({ ...f, original_price: e.target.value }))} step="0.01" className="input-field" placeholder="Optional MRP" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <input type="text" value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} className="input-field" placeholder="kurta, saree, western..." />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Material</label>
                  <input type="text" value={form.material} onChange={e => setForm(f => ({ ...f, material: e.target.value }))} className="input-field" />
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" id="featured" checked={form.is_featured} onChange={e => setForm(f => ({ ...f, is_featured: e.target.checked }))} className="h-4 w-4 text-primary-600" />
                  <label htmlFor="featured" className="text-sm font-medium text-gray-700">Featured Product</label>
                </div>
              </div>

              {/* ── Color × Size variant matrix ─────────────────────── */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-semibold text-gray-700">Color &amp; Size Stock</p>
                  <span className="text-xs text-gray-400">Total: {totalStock} units</span>
                </div>

                {/* Matrix table */}
                {colorRows.length > 0 && (
                  <div className="overflow-x-auto mb-3 rounded-xl border border-gray-200">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="text-left px-3 py-2 text-gray-500 font-medium w-36">Color</th>
                          {SIZES.map(s => (
                            <th key={s} className="text-center px-2 py-2 text-gray-500 font-medium">{s}</th>
                          ))}
                          <th className="w-8" />
                        </tr>
                      </thead>
                      <tbody>
                        {colorRows.map((row, ri) => {
                          const rowTotal = SIZES.reduce((s, sz) => s + (row.stock[sz] || 0), 0);
                          return (
                            <tr key={ri} className="border-t border-gray-100">
                              <td className="px-3 py-2">
                                <div className="flex items-center gap-2">
                                  <input
                                    type="color"
                                    value={row.hex_code}
                                    onChange={e => setColorRows(prev => prev.map((r, i) => i === ri ? { ...r, hex_code: e.target.value } : r))}
                                    className="w-6 h-6 rounded-full border-0 cursor-pointer p-0"
                                    title="Change color"
                                  />
                                  <span className="font-medium text-gray-700 truncate max-w-[80px]" title={row.name}>{row.name}</span>
                                  {rowTotal === 0 && <span className="text-xs text-red-400 font-medium">OUT</span>}
                                </div>
                              </td>
                              {SIZES.map(size => (
                                <td key={size} className="px-1 py-1 text-center">
                                  <input
                                    type="number"
                                    min="0"
                                    value={row.stock[size]}
                                    onChange={e => updateStock(ri, size, parseInt(e.target.value) || 0)}
                                    className={`w-12 text-center rounded-lg border py-1 text-sm focus:outline-none focus:ring-1 focus:ring-primary-400 ${
                                      row.stock[size] === 0 ? "border-red-200 bg-red-50 text-red-600" : "border-gray-200"
                                    }`}
                                  />
                                </td>
                              ))}
                              <td className="px-2 py-2 text-center">
                                <button type="button" onClick={() => removeColorRow(ri)} className="text-gray-300 hover:text-red-500 transition-colors">
                                  <XMarkIcon className="h-4 w-4" />
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Preset color chips */}
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {PRESET_COLORS.filter(pc => !colorRows.some(r => r.name.toLowerCase() === pc.name.toLowerCase())).map(pc => (
                    <button
                      key={pc.name}
                      type="button"
                      onClick={() => addColorRow(pc.name, pc.hex_code)}
                      className="flex items-center gap-1 px-2.5 py-1 text-xs border border-gray-200 rounded-full hover:border-primary-400 hover:bg-primary-50 transition-colors"
                    >
                      <span className="w-3 h-3 rounded-full border border-gray-300 flex-shrink-0" style={{ backgroundColor: pc.hex_code }} />
                      {pc.name}
                    </button>
                  ))}
                </div>

                {/* Custom color input */}
                <div className="flex gap-2 items-center">
                  <input
                    type="color"
                    value={newColorHex}
                    onChange={e => setNewColorHex(e.target.value)}
                    className="h-9 w-10 rounded border border-gray-300 cursor-pointer p-0.5"
                  />
                  <input
                    type="text"
                    value={newColorName}
                    onChange={e => setNewColorName(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addColorRow(newColorName, newColorHex); } }}
                    placeholder="Custom color name (e.g. Rose Gold)"
                    className="input-field flex-1 py-1.5 text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => addColorRow(newColorName, newColorHex)}
                    className="btn-secondary py-1.5 px-3 text-sm flex-shrink-0"
                  >
                    + Add
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-1">Each color row has individual stock per size. Red cell = out of stock for that size.</p>
              </div>

              <div className="flex gap-3 pt-2">
                <button type="submit" disabled={saving} className="btn-primary px-6 py-2.5">
                  {saving ? "Saving..." : editProduct ? "Update" : "Create Product"}
                </button>
                <button type="button" onClick={() => { setShowForm(false); resetForm(); }} className="btn-secondary px-6 py-2.5">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Products table */}
      {loading ? (
        <LoadingSpinner className="py-20" size="lg" />
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="text-left px-5 py-3 text-gray-500 font-medium">Product</th>
                  <th className="text-left px-3 py-3 text-gray-500 font-medium">Colors</th>
                  <th className="text-left px-3 py-3 text-gray-500 font-medium">Price</th>
                  <th className="text-left px-3 py-3 text-gray-500 font-medium">Stock</th>
                  <th className="text-left px-3 py-3 text-gray-500 font-medium">Status</th>
                  <th className="text-right px-5 py-3 text-gray-500 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {products.map((product) => {
                  // Get unique colors from variants
                  const uniqueColors: { name: string; hex_code: string }[] = [];
                  for (const v of (product.variants || [])) {
                    if (v.color_name && !uniqueColors.some(c => c.name === v.color_name)) {
                      uniqueColors.push({ name: v.color_name, hex_code: v.hex_code });
                    }
                  }
                  return (
                    <tr key={product.id} className="border-b border-gray-50 hover:bg-gray-50">
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-3">
                          <img
                            src={resolveImageUrl(
                              product.images?.find((i: any) => i.is_primary)?.url || product.images?.[0]?.url,
                              "https://via.placeholder.com/40/f9a8d4/ec4899?text=D"
                            )}
                            alt="" className="w-10 h-12 object-cover rounded-lg flex-shrink-0"
                          />
                          <div>
                            <p className="font-medium text-gray-800">{product.name}</p>
                            <p className="text-xs text-gray-400">{product.category}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex gap-1 flex-wrap">
                          {uniqueColors.length > 0 ? uniqueColors.map(c => (
                            <span
                              key={c.name}
                              className="w-5 h-5 rounded-full border border-gray-300 inline-block"
                              style={{ backgroundColor: c.hex_code }}
                              title={c.name}
                            />
                          )) : <span className="text-xs text-gray-400">—</span>}
                        </div>
                      </td>
                      <td className="px-3 py-3 font-medium text-primary-600">
                        ₹{Number(product.price).toLocaleString("en-IN")}
                      </td>
                      <td className="px-3 py-3">
                        {product.total_stock === 0 ? (
                          <span className="badge bg-red-100 text-red-700">Out of Stock</span>
                        ) : (
                          <span className={product.total_stock < 5 ? "text-red-600 font-medium" : "text-gray-600"}>
                            {product.total_stock} {product.total_stock < 5 && "(Low)"}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        <span className={`badge ${product.is_active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                          {product.is_active ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => setManageImagesFor(product)}
                            className="flex items-center gap-1 px-2 py-1 text-xs bg-primary-50 text-primary-600 hover:bg-primary-100 rounded-lg transition-colors font-medium"
                          >
                            <PhotoIcon className="h-3.5 w-3.5" />
                            Images ({product.images?.length || 0})
                          </button>
                          <button onClick={() => handleEdit(product)} className="p-1.5 text-gray-400 hover:text-blue-600 transition-colors">
                            <PencilIcon className="h-4 w-4" />
                          </button>
                          <button onClick={() => handleDelete(product.id)} className="p-1.5 text-gray-400 hover:text-red-600 transition-colors">
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
